/**
 * useAjax is a custom hook for making XhrIO AJAX requests, inspired by react-query. It can handle
 * asynchronous requests that are triggered automatically on parameter changes, dependent queries,
 * and manual fetching depending on the parameters it is passed. It returns fields to get the
 * current query status, the latest query data, and callbacks to manage requests.
 *
 * Simple Fetch: this example creates a hook that will trigger when the page loads, calling a
 * function `myRequest` that takes two arguments (here passed as myParam1, myParam2). The request
 * is made on page load and any time myParam1 or myParam2 change values.
 * ```
 *   const {status, data} = useAjax(true, myRequest, myParam1, myParam2);
 * ```
 *
 * Manual Fetch: this example creates a hook that will never trigger on its own (eg, without any
 * useEffect behavior), even when its parameters change. This is useful when an action should be
 * triggered manually by the user, such as when they click a button. The button onClick should call
 * the refetch function with no params (these are pulled from the param list in useAjax).
 * ```
 *   const {status, data, refetch} = useAjax(false, myRequest, myParam1);
 * ```
 *
 * Dependent Fetch: this example creates a hook that will trigger only once valid data is available
 * for the query. This would be the case when the use must choose data first, or when one query
 * depends on the result of another. Any time the first argument resolves to false, no queries will
 * be made automatically, but once the value is true, any parameter changes will trigger a query.
 * ```
 *   const {status, data} = useAjax(myParam1 !== undefined, myRequest, myParam1);
 * ```
 *
 * @param {?} automatic Converted to boolean to determine if the query should trigger automatically.
 * @param {!Function} requestFn The async/request function to call, which returns a Promise<?>.
 * @param {...*} params The parameters to pass to the request function on fetch/refetch.
 * @return {{
 *   status: (string | undefined),
 *   data: (? | undefined),
 *   refetch: function(),
 *   abort: function(),
 * }}
 */
export function useAjax(automatic, requestFn, ...params) {
  const isAutomatic = !!automatic;

  const abortController = React.useRef(new AbortController());
  const [state, dispatch] = React.useReducer(onDispatch, {
    status: undefined,
    data: undefined,
  });

  const refetch = React.useCallback(() => {
    abortController.current.abort();
    dispatch({type: 'loading'});

    abortController.current = new AbortController();
    requestFn(...params, abortController.current.signal)
      .then(data => dispatch({type: 'success', data}))
      .catch(aborted => !aborted && dispatch({type: 'error'}));
  }, [...params, requestFn] /* eslint-disable-line react-hooks/exhaustive-deps */);

  React.useEffect(() => {
    if (isAutomatic) {
      refetch();
      return () => abortController.current.abort();
    }
  }, [refetch, isAutomatic]);

  const abort = React.useCallback(() => {
    abortController.current.abort();
    dispatch({type: 'aborted'});
  }, []);

  return {
    status: state.status,
    data: state.data,
    refetch,
    abort,
  };
}

function onDispatch(state, action) {
  switch (action.type) {
    case 'loading':
      return {...state, status: 'loading'};
    case 'success':
      return {status: 'success', data: action.data};
    case 'error':
      return {status: 'error', data: undefined};
    case 'aborted':
      if (state.status !== 'loading') {
        return state;
      } else if (state.data !== undefined) {
        return {...state, status: 'success'};
      } else {
        return {status: undefined, data: undefined};
      }
    default:
      throw new Error(`unhandled useAjax action '${action.type}'`);
  }
}
